import { useMutation, useQueryClient } from '@tanstack/react-query';
import cloneDeep from 'lodash/cloneDeep';
import difference from 'lodash/difference';

import { QUERY_KEYS } from '@hero/constants';
import { contactServices, recognitionServices } from '@hero/services';

/**
 * Add or remove recognition recipients with contact ids
 */
async function updateRecipientsByChunk({ queryClient, code, contactIds, isRemove = false }) {
  const clonedContactIds = cloneDeep(contactIds);

  while (clonedContactIds.length > 0) {
    const chunkedIds = clonedContactIds.splice(0, 2000);

    if (isRemove) {
      await recognitionServices.recogRecipientsDelete({
        code,
        contactRefIds: chunkedIds
      });
    } else {
      const contacts = await queryClient.fetchQuery(
        [QUERY_KEYS.useGetContactsKey, chunkedIds.join(',')],
        () => {
          return contactServices.getContacts({
            filters: { ids: chunkedIds },
            options: { pagination: false }
          });
        }
      );

      await recognitionServices.recogRecipientsBulkUpsert({
        params: {
          code
        },
        body: {
          recipients: contacts.docs.map(contact => ({
            email: contact.email,
            firstName: contact.firstName,
            lastName: contact.lastName,
            contact: {
              id: contact.id,
              source: 'contact'
            }
          }))
        }
      });
    }
  }
}

export const useRecogRecipientsBulkUpsert = (options = {}) => {
  const queryClient = useQueryClient();

  return useMutation(
    async ({ params, body, contactIds, recipientContactIds }) => {
      if (contactIds?.length) {
        const removeContactIds = difference(recipientContactIds, contactIds);
        const addContactIds = difference(contactIds, recipientContactIds);

        await updateRecipientsByChunk({
          queryClient,
          contactIds: removeContactIds,
          code: params.code,
          isRemove: true
        });

        await updateRecipientsByChunk({
          queryClient,
          contactIds: addContactIds,
          code: params.code
        });

        return true;
      }

      return recognitionServices.recogRecipientsBulkUpsert({ params, body });
    },
    {
      ...options,
      onSuccess(data, variables) {
        queryClient.invalidateQueries({
          queryKey: ['recognition-recipients', variables?.params.code],
          refetchType: 'active'
        });

        queryClient.invalidateQueries({
          queryKey: ['recognitionSpendingSummary', variables?.params.code],
          refetchType: 'active'
        });
      }
    }
  );
};
